import React, { useState, useRef, useEffect } from 'react';
import { BatchRecord } from '../../../types/batchRecord';
import { BatchPan } from '../../../types/batchPan';
import { Recipe } from '../../../types/recipe';
import { BatchRecordIngredientsTable } from './BatchRecordIngredientsTable';
import { ProcessingParametersTable } from './ProcessingParametersTable';
import { SignOffSection } from './SignOffSection';
import { batchPanApiService } from '../../../services/batchPanApiService';
import { ClickableIdentifier } from '../../../components/haccp/ClickableIdentifier';
import { useTraceability } from '../../../components/haccp/TraceabilityContext';
import { scrollElementToCenter, scrollToFieldAndHighlight, BATCH_SIGN_OFF_FIELDS, getBatchFieldElementId } from '../../../utils/scrollUtils';

interface BatchRecordCardProps {
  record: BatchRecord;
  fillings: Recipe[];
  isEditMode: boolean;
  onUpdate: (record: BatchRecord) => void;
  onDelete: () => void;
  onEditToggle: () => void;
  initialExpanded?: boolean;
  highlightOnMount?: boolean;
  targetField?: string;
  isFlagged?: boolean;
}

export const BatchRecordCard: React.FC<BatchRecordCardProps> = ({
  record,
  fillings,
  isEditMode,
  onUpdate,
  onDelete,
  onEditToggle,
  initialExpanded = false,
  highlightOnMount = false,
  targetField,
  isFlagged
}) => {
  const { openTraceability } = useTraceability();
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const [isHighlighted, setIsHighlighted] = useState(highlightOnMount);
  const cardRef = useRef<HTMLDivElement>(null);
  const [editedRecord, setEditedRecord] = useState<BatchRecord>(record);
  const [isIngredientsExpanded, setIsIngredientsExpanded] = useState(false);
  const [isProcessingExpanded, setIsProcessingExpanded] = useState(false);
  const [isSignOffExpanded, setIsSignOffExpanded] = useState(false);
  const [pans, setPans] = useState<BatchPan[]>([]);
  const [pansLoaded, setPansLoaded] = useState(false);

  // Handle highlighting and scrolling when component mounts with highlightOnMount
  useEffect(() => {
    if (highlightOnMount) {
      // Delay scroll so layout settles after section expansion
      const scrollTimer = setTimeout(() => {
        if (cardRef.current) scrollElementToCenter(cardRef.current);
      }, 300);

      // Remove highlight after 3 seconds
      const highlightTimer = setTimeout(() => {
        setIsHighlighted(false);
      }, 3000);

      return () => { clearTimeout(scrollTimer); clearTimeout(highlightTimer); };
    }
  }, [highlightOnMount]);

  // Field-level deep-linking: expand sign-off if needed, then scroll to specific field
  useEffect(() => {
    if (!targetField) return;
    if (BATCH_SIGN_OFF_FIELDS.has(targetField)) {
      setIsExpanded(true);
      setIsSignOffExpanded(true);
    }
    const timer = setTimeout(() => {
      scrollToFieldAndHighlight(getBatchFieldElementId(record.id, targetField), {
        highlightColor: isFlagged ? 'red' : 'blue'
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [targetField, record.id, isFlagged]);

  // Fetch pans when processing section is expanded
  useEffect(() => {
    if (isProcessingExpanded && !pansLoaded) {
      batchPanApiService.fetchPansByBatchRecord(record.id).then(fetchedPans => {
        setPans(fetchedPans);
        setPansLoaded(true);
      }).catch(err => {
        console.error('Error fetching pans:', err);
        setPansLoaded(true);
      });
    }
  }, [isProcessingExpanded, pansLoaded, record.id]);

  const handlePanUpdate = async (updatedPan: BatchPan) => {
    try {
      const saved = await batchPanApiService.updatePan(updatedPan.id, updatedPan);
      setPans(prev => prev.map(p => (p.id === saved.id ? saved : p)));
    } catch (err) {
      console.error('Error updating pan:', err);
    }
  };

  const handleFieldChange = (field: keyof BatchRecord, value: string) => {
    if (field === 'productId') {
      const selectedFilling = fillings.find(f => f.id === value);
      setEditedRecord({
        ...editedRecord,
        productId: value,
        productName: selectedFilling?.name || ''
      });
    } else {
      setEditedRecord({
        ...editedRecord,
        [field]: value
      });
    }
  };

  const handleSave = () => {
    onUpdate(editedRecord);
    onEditToggle();
  };

  const handleCancel = () => {
    setEditedRecord(record);
    onEditToggle();
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s'
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box'
  };

  return (
    <div
      ref={cardRef}
      id={`batch-record-${record.id}`}
      style={{
        border: `2px solid ${isHighlighted ? '#2196F3' : '#ddd'}`,
        borderRadius: '8px',
        marginBottom: '16px',
        backgroundColor: 'white',
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        boxShadow: isHighlighted ? '0 0 20px rgba(33, 150, 243, 0.5)' : 'none'
      }}
    >
      {/* Card Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          padding: '16px',
          backgroundColor: '#f5f5f5',
          borderBottom: isExpanded ? '1px solid #ddd' : 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#eeeeee')}
        onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
      >
        <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>{record.productName || 'New Batch Record'}</h4>
        <span
          style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#666',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            display: 'inline-block'
          }}
        >
          ▼
        </span>
      </div>

      {/* Card Content */}
      <div style={{ padding: '20px' }}>
        {/* Collapsed View - Table of Fields */}
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '16px' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}
          >
          <thead>
            <tr>
              <th
                style={{
                  padding: '12px 8px',
                  backgroundColor: '#f8f9fa',
                  fontWeight: '600',
                  textAlign: 'left',
                  borderBottom: '2px solid #e0e0e0',
                  textTransform: 'uppercase',
                  fontSize: '12px',
                  letterSpacing: '0.5px',
                  color: '#555'
                }}
              >
                Product Name
              </th>
              <th
                style={{
                  padding: '12px 8px',
                  backgroundColor: '#f8f9fa',
                  fontWeight: '600',
                  textAlign: 'left',
                  borderBottom: '2px solid #e0e0e0',
                  textTransform: 'uppercase',
                  fontSize: '12px',
                  letterSpacing: '0.5px',
                  color: '#555'
                }}
              >
                Date of Production
              </th>
              <th
                style={{
                  padding: '12px 8px',
                  backgroundColor: '#f8f9fa',
                  fontWeight: '600',
                  textAlign: 'left',
                  borderBottom: '2px solid #e0e0e0',
                  textTransform: 'uppercase',
                  fontSize: '12px',
                  letterSpacing: '0.5px',
                  color: '#555'
                }}
              >
                Batch/Lot #
              </th>
              <th
                style={{
                  padding: '12px 8px',
                  backgroundColor: '#f8f9fa',
                  fontWeight: '600',
                  textAlign: 'left',
                  borderBottom: '2px solid #e0e0e0',
                  textTransform: 'uppercase',
                  fontSize: '12px',
                  letterSpacing: '0.5px',
                  color: '#555'
                }}
              >
                Prepared By
              </th>
              <th
                style={{
                  padding: '12px 8px',
                  backgroundColor: '#f8f9fa',
                  fontWeight: '600',
                  textAlign: 'left',
                  borderBottom: '2px solid #e0e0e0',
                  textTransform: 'uppercase',
                  fontSize: '12px',
                  letterSpacing: '0.5px',
                  color: '#555'
                }}
              >
                Verified By
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td id={`batch-${record.id}-field-productName`} style={{ padding: '12px 8px', borderBottom: '1px solid #eee' }}>
                {isEditMode ? (
                  <select
                    value={editedRecord.productId}
                    onChange={e => handleFieldChange('productId', e.target.value)}
                    style={inputStyle}
                  >
                    <option value="">Select a product...</option>
                    {fillings.map(filling => (
                      <option key={filling.id} value={filling.id}>
                        {filling.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  record.productName || '-'
                )}
              </td>
              <td id={`batch-${record.id}-field-dateOfProduction`} style={{ padding: '12px 8px', borderBottom: '1px solid #eee' }}>
                {isEditMode ? (
                  <input
                    type="date"
                    value={editedRecord.dateOfProduction}
                    onChange={e => handleFieldChange('dateOfProduction', e.target.value)}
                    style={inputStyle}
                  />
                ) : (
                  record.dateOfProduction ? record.dateOfProduction.split('T')[0] : '-'
                )}
              </td>
              <td id={`batch-${record.id}-field-batchLotNumber`} style={{ padding: '12px 8px', borderBottom: '1px solid #eee' }}>
                {isEditMode ? (
                  <input
                    type="text"
                    value={editedRecord.batchLotNumber}
                    onChange={e => handleFieldChange('batchLotNumber', e.target.value)}
                    style={inputStyle}
                    placeholder="Batch/Lot #"
                  />
                ) : (
                  record.batchLotNumber ? (
                    <ClickableIdentifier value={record.batchLotNumber} type="batchLotNumber" onClick={openTraceability} />
                  ) : '-'
                )}
              </td>
              <td id={`batch-${record.id}-field-preparedBy`} style={{ padding: '12px 8px', borderBottom: '1px solid #eee' }}>
                {isEditMode ? (
                  <input
                    type="text"
                    value={editedRecord.preparedBy}
                    onChange={e => handleFieldChange('preparedBy', e.target.value)}
                    style={inputStyle}
                    placeholder="Prepared by"
                  />
                ) : (
                  record.preparedBy || '-'
                )}
              </td>
              <td id={`batch-${record.id}-field-verifiedBy`} style={{ padding: '12px 8px', borderBottom: '1px solid #eee' }}>
                {isEditMode ? (
                  <input
                    type="text"
                    value={editedRecord.verifiedBy}
                    onChange={e => handleFieldChange('verifiedBy', e.target.value)}
                    style={inputStyle}
                    placeholder="Verified by"
                  />
                ) : (
                  record.verifiedBy || '-'
                )}
              </td>
            </tr>
          </tbody>
        </table>
        </div>

        {/* Expanded View - Three Sections */}
        {isExpanded && (
          <div style={{ marginBottom: '16px' }}>
            {/* Ingredients and Preparation Section */}
            <div
              id={`batch-${record.id}-ingredients`}
              style={{
                marginBottom: '16px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                overflow: 'hidden'
              }}
            >
              <div
                onClick={() => setIsIngredientsExpanded(!isIngredientsExpanded)}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  borderBottom: isIngredientsExpanded ? '1px solid #e0e0e0' : 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#555',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#eeeeee')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
              >
                Ingredients and Preparation
                <span
                  style={{
                    fontSize: '1rem',
                    color: '#666',
                    transition: 'transform 0.2s ease-in-out',
                    transform: isIngredientsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    display: 'inline-block'
                  }}
                >
                  ▶
                </span>
              </div>
              {isIngredientsExpanded && (
                <div
                  style={{
                    padding: '20px',
                    backgroundColor: '#fafafa'
                  }}
                >
                  <BatchRecordIngredientsTable batchRecordId={record.id} />
                </div>
              )}
            </div>

            {/* Processing Parameters Section */}
            <div
              id={`batch-${record.id}-processing`}
              style={{
                marginBottom: '16px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                overflow: 'hidden'
              }}
            >
              <div
                onClick={() => setIsProcessingExpanded(!isProcessingExpanded)}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  borderBottom: isProcessingExpanded ? '1px solid #e0e0e0' : 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#555',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#eeeeee')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
              >
                Processing Parameters{pansLoaded && pans.length > 0 ? ` (${pans.length} pan${pans.length !== 1 ? 's' : ''})` : ''}
                <span
                  style={{
                    fontSize: '1rem',
                    color: '#666',
                    transition: 'transform 0.2s ease-in-out',
                    transform: isProcessingExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    display: 'inline-block'
                  }}
                >
                  ▶
                </span>
              </div>
              {isProcessingExpanded && (
                <div
                  style={{
                    padding: '20px',
                    backgroundColor: '#fafafa'
                  }}
                >
                  <ProcessingParametersTable record={record} pans={pans} onPanUpdate={handlePanUpdate} />
                </div>
              )}
            </div>

            {/* Sign-Off Section */}
            <div
              id={`batch-${record.id}-sign-off`}
              style={{
                marginBottom: '16px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                overflow: 'hidden'
              }}
            >
              <div
                onClick={() => setIsSignOffExpanded(!isSignOffExpanded)}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  borderBottom: isSignOffExpanded ? '1px solid #e0e0e0' : 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#555',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#eeeeee')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
              >
                Sign-Off
                <span
                  style={{
                    fontSize: '1rem',
                    color: '#666',
                    transition: 'transform 0.2s ease-in-out',
                    transform: isSignOffExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    display: 'inline-block'
                  }}
                >
                  ▶
                </span>
              </div>
              {isSignOffExpanded && (
                <div
                  style={{
                    padding: '20px',
                    backgroundColor: '#fafafa'
                  }}
                >
                  <SignOffSection record={record} onUpdate={onUpdate} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {isEditMode ? (
            <>
              <button
                onClick={handleSave}
                style={{
                  ...buttonStyle,
                  backgroundColor: '#4CAF50',
                  color: 'white'
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                style={{
                  ...buttonStyle,
                  backgroundColor: '#757575',
                  color: 'white'
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#616161')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#757575')}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onEditToggle}
                style={{
                  ...buttonStyle,
                  backgroundColor: '#2196F3',
                  color: 'white'
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#1976D2')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#2196F3')}
              >
                Edit
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Delete this batch record? This cannot be undone.')) {
                    onDelete();
                  }
                }}
                style={{
                  ...buttonStyle,
                  backgroundColor: '#f44336',
                  color: 'white'
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#d32f2f')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f44336')}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
